import React from 'react';
import { Box, CircularProgress, Paper, Portal, Typography } from '@mui/material';

type LoadingOverlayProps = {
  open: boolean;
  title: string;
  detail?: string;
};

/**
 * Blocks the page while a change that must not be interrupted is saved, such
 * as deleting a team member. Same look as LoadingSpinner, on a light card.
 */
// MUI's Backdrop marks itself aria-hidden, which would hide this message from
// screen readers, so the overlay is a plain fixed layer instead.
const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ open, title, detail }) => (open ? (
  <Portal>
  <Box
    sx={{
      position: 'fixed',
      inset: 0,
      zIndex: (theme) => theme.zIndex.modal + 2,
      display: 'grid',
      placeItems: 'center',
      p: 2,
      bgcolor: 'rgba(248,250,252,.72)',
      backdropFilter: 'blur(6px)',
    }}
  >
    <Paper
      role="alertdialog"
      aria-busy="true"
      aria-label={title}
      elevation={0}
      sx={{
        px: 4,
        py: 3.5,
        minWidth: 260,
        maxWidth: 360,
        textAlign: 'center',
        borderRadius: 4,
        border: '1px solid rgba(15,23,42,.08)',
        boxShadow: '0 24px 70px rgba(15,23,42,.16)',
      }}
    >
      <Box sx={{ position: 'relative', width: 44, height: 44, mx: 'auto', mb: 2 }}>
        <CircularProgress variant="determinate" value={100} size={44} thickness={4}
          sx={{ position: 'absolute', inset: 0, color: 'rgba(25,118,210,.14)' }} />
        <CircularProgress size={44} thickness={4} disableShrink
          sx={{ position: 'absolute', inset: 0, '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
      </Box>
      <Typography fontWeight={900}>{title}</Typography>
      {detail ? <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>{detail}</Typography> : null}
    </Paper>
  </Box>
  </Portal>
) : null);

export default LoadingOverlay;
